import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
    getAdminPaymentMethods,
    getPaymentGatewayOverview,
    saveAdminPaymentMethods,
    testGatewayConnection,
} from '../../api/paymentMethods';
import Button from '../../components/common/Button';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import { formatDateTime } from '../../utils/date';
import { getCountryProfile } from '../../config/countryProfiles';
import { useRegionalSettings } from '../../context/RegionalSettingsContext';
import { TextInput } from '../../components/common/form';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';
import { apiErrorMessage } from '../../utils/apiError';

const PAYPAL_CURRENCIES = new Set(['USD', 'EUR', 'GBP', 'AUD', 'CAD', 'INR', 'SGD', 'AED', 'JPY', 'CHF', 'NZD', 'HKD']);
const MOLLIE_CURRENCIES = new Set(['EUR', 'GBP', 'USD', 'CHF', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF']);

function supportsCurrency(provider, currencyCode) {
    const currency = String(currencyCode || '').toUpperCase();
    if (!currency) return true;
    if (provider === 'RAZORPAY') return currency === 'INR' || currency === 'USD';
    if (provider === 'STRIPE') return currency.length === 3;
    if (provider === 'PAYPAL') return PAYPAL_CURRENCIES.has(currency);
    if (provider === 'MOLLIE') return MOLLIE_CURRENCIES.has(currency);
    return false;
}

const GATEWAYS = [
    {
        key: 'GATEWAY_RAZORPAY',
        provider: 'RAZORPAY',
        name: 'Razorpay',
        subtitle: 'UPI · Cards · Net Banking · Wallets',
        keyIdLabel: 'Razorpay Key ID',
        keySecretLabel: 'Razorpay Key Secret',
        keyIdPlaceholder: 'rzp_live_xxxxxxxxxxxxxxxx',
        keySecretPlaceholder: 'rzp_live_xxxxxxxxxxxxxxxx',
        keysUrl: 'https://dashboard.razorpay.com/app/keys',
        webhookPath: '/api/webhooks/razorpay',
        webhookEvents: 'payment.captured',
        needsWebhook: true,
        webhookOptional: true,
        webhookLabel: 'Razorpay Webhook Secret (optional backup)',
        webhookPlaceholder: 'From Razorpay Dashboard → Webhooks',
    },
    {
        key: 'GATEWAY_STRIPE',
        provider: 'STRIPE',
        name: 'Stripe',
        subtitle: 'Cards · Apple Pay · Google Pay · Local methods',
        keyIdLabel: 'Stripe Publishable Key',
        keySecretLabel: 'Stripe Secret Key',
        keyIdPlaceholder: 'pk_live_xxxxxxxxxxxxxxxx',
        keySecretPlaceholder: 'sk_live_xxxxxxxxxxxxxxxx',
        keysUrl: 'https://dashboard.stripe.com/apikeys',
        webhookPath: '/api/webhooks/stripe',
        webhookEvents: 'checkout.session.completed',
        needsWebhook: true,
        webhookLabel: 'Stripe Webhook Signing Secret',
        webhookPlaceholder: 'whsec_xxxxxxxxxxxxxxxx',
    },
    {
        key: 'GATEWAY_PAYPAL',
        provider: 'PAYPAL',
        name: 'PayPal',
        subtitle: 'PayPal balance · Cards',
        keyIdLabel: 'PayPal Client ID',
        keySecretLabel: 'PayPal Client Secret',
        keyIdPlaceholder: 'Client ID from developer.paypal.com',
        keySecretPlaceholder: 'Client Secret',
        keysUrl: 'https://developer.paypal.com/dashboard/applications/sandbox',
        webhookPath: '/api/webhooks/paypal',
        webhookEvents: 'PAYMENT.CAPTURE.COMPLETED',
        needsWebhook: true,
        webhookOptional: true,
        webhookLabel: 'PayPal Webhook ID',
        webhookPlaceholder: 'Webhook ID from developer.paypal.com',
    },
    {
        key: 'GATEWAY_MOLLIE',
        provider: 'MOLLIE',
        name: 'Mollie',
        subtitle: 'iDEAL · Bancontact · Cards · SEPA (Europe)',
        keyIdLabel: 'Mollie Profile ID (optional)',
        keySecretLabel: 'Mollie API Key',
        keyIdPlaceholder: 'pfl_… or leave blank',
        keySecretPlaceholder: 'test_… or live_…',
        keysUrl: 'https://my.mollie.com/dashboard/developers/api-keys',
        webhookPath: '/api/webhooks/mollie',
        webhookEvents: 'payment paid (form id=tr_…)',
        needsWebhook: false,
        webhookOptional: true,
        webhookLabel: 'Unused (Mollie verifies via API fetch)',
        webhookPlaceholder: '',
    },
];

function StatusChip({ on, t }) {
    return (
        <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
                on ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'
            }`}
        >
            {on ? t('settings.report.statusOn') : t('settings.report.statusOff')}
        </span>
    );
}

function GatewayLogo({ provider }) {
    if (provider === 'RAZORPAY') {
        return (
            <svg viewBox="0 0 32 32" className="h-8 w-8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" rx="8" fill="#2D6BE4" />
                <text x="4" y="23" fontFamily="Arial" fontWeight="bold" fontSize="14" fill="white">Rz</text>
            </svg>
        );
    }
    if (provider === 'STRIPE') {
        return (
            <svg viewBox="0 0 32 32" className="h-8 w-8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" rx="8" fill="#635BFF" />
                <text x="6" y="22" fontFamily="Arial" fontWeight="bold" fontSize="13" fill="white">S</text>
            </svg>
        );
    }
    if (provider === 'MOLLIE') {
        return (
            <svg viewBox="0 0 32 32" className="h-8 w-8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" rx="8" fill="#000000" />
                <text x="4" y="22" fontFamily="Arial" fontWeight="bold" fontSize="11" fill="white">Mo</text>
            </svg>
        );
    }
    return (
        <svg viewBox="0 0 32 32" className="h-8 w-8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" rx="8" fill="#003087" />
            <text x="5" y="22" fontFamily="Arial" fontWeight="bold" fontSize="12" fill="white">PP</text>
        </svg>
    );
}

function GatewayCard({
    config,
    method,
    newSecrets,
    testState,
    onToggle,
    onFieldChange,
    onSecretChange,
    onWebhookSecretChange,
    onTestConnection,
    webhookUrl,
    currencyCode,
    licensed,
    httpsReady,
}) {
    const { t } = useTranslation();
    const providerKey = String(config.provider || '').toLowerCase();
    const subtitle = t(`settings.payment.gateways.${providerKey}.subtitle`, { defaultValue: config.subtitle });
    const keyIdLabel = t(`settings.payment.gateways.${providerKey}.keyIdLabel`, { defaultValue: config.keyIdLabel });
    const keySecretLabel = t(`settings.payment.gateways.${providerKey}.keySecretLabel`, { defaultValue: config.keySecretLabel });
    const webhookLabel = t(`settings.payment.gateways.${providerKey}.webhookLabel`, { defaultValue: config.webhookLabel });
    const keyIdPlaceholder = t(`settings.payment.gateways.${providerKey}.keyIdPlaceholder`, { defaultValue: config.keyIdPlaceholder });
    const keySecretPlaceholder = t(`settings.payment.gateways.${providerKey}.keySecretPlaceholder`, { defaultValue: config.keySecretPlaceholder });
    const webhookPlaceholder = t(`settings.payment.gateways.${providerKey}.webhookPlaceholder`, { defaultValue: config.webhookPlaceholder });
    const isEnabled = !!method?.enabled;
    const apiKeyConfigured = method?.gatewayKeySecret === 'CONFIGURED';
    const webhookConfigured = method?.gatewayWebhookSecret === 'CONFIGURED';
    const newSecret = newSecrets[config.key] || '';
    const newWebhookSecret = newSecrets[`${config.key}_webhook`] || '';
    const canEnable = apiKeyConfigured || !!newSecret.trim();
    const webhookRequired = config.needsWebhook && !config.webhookOptional;
    const canEnableWebhook = !webhookRequired || webhookConfigured || !!newWebhookSecret.trim();
    const cardTest = testState || {};
    const currencyOk = supportsCurrency(config.provider, currencyCode);
    const httpsOk = !config.key.includes('STRIPE') && !config.key.includes('PAYPAL') && !config.key.includes('MOLLIE')
        ? true
        : httpsReady;

    return (
        <div
            className={`overflow-hidden rounded-xl border transition-colors ${
                isEnabled
                    ? 'border-primary-200 bg-primary-50/30'
                    : 'border-slate-200 bg-white'
            }`}
        >
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100/80 bg-white/70 px-4 py-4 sm:px-5">
                <div className="flex min-w-0 items-center gap-3">
                    <GatewayLogo provider={config.provider} />
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold text-slate-900">{config.name}</p>
                            <StatusChip on={isEnabled} t={t} />
                        </div>
                        <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
                    </div>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={isEnabled}
                    onClick={() => licensed && onToggle(config.key, !isEnabled)}
                    disabled={!licensed}
                    className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${!licensed ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${isEnabled ? 'bg-primary-600' : 'bg-slate-200'}`}
                >
                    <span
                        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 ${isEnabled ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                </button>
            </div>

            <div className="space-y-4 px-4 py-4 sm:px-5">
                <p className="text-sm text-slate-600">
                    {isEnabled
                        ? t('settings.payment.gatewayActive', { name: config.name })
                        : t('settings.payment.gatewayInactive', { name: config.name })}
                </p>

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <TextInput
                        label={keySecretLabel}
                        type="password"
                        className="font-mono text-sm"
                        placeholder={apiKeyConfigured ? t('settings.payment.secretKeepPlaceholder') : keySecretPlaceholder}
                        value={newSecret}
                        onChange={(e) => onSecretChange(config.key, e.target.value)}
                        autoComplete="new-password"
                        helper={apiKeyConfigured && !newSecret
                            ? t('settings.payment.secretSetHelper')
                            : !apiKeyConfigured && !newSecret
                                ? t('settings.payment.secretNotSetHelper')
                                : undefined}
                    />
                    <TextInput
                        label={keyIdLabel}
                        className="font-mono text-sm"
                        placeholder={keyIdPlaceholder}
                        value={method?.gatewayKeyId || ''}
                        onChange={(e) => onFieldChange(config.key, 'gatewayKeyId', e.target.value)}
                    />
                </div>

                {config.provider === 'PAYPAL' && (
                    <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
                        <div>
                            <p className="text-sm font-medium text-slate-800">{t('settings.payment.paypalSandbox')}</p>
                            <p className="text-xs text-slate-500">{t('settings.payment.paypalSandboxHint')}</p>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={method?.paypalSandbox !== false}
                            onClick={() => onFieldChange(config.key, 'paypalSandbox', method?.paypalSandbox === false)}
                            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors ${method?.paypalSandbox !== false ? 'bg-primary-600' : 'bg-slate-200'}`}
                        >
                            <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${method?.paypalSandbox !== false ? 'translate-x-5' : 'translate-x-0'}`} />
                        </button>
                    </div>
                )}

                {config.needsWebhook && (
                    <TextInput
                        label={webhookLabel}
                        type="password"
                        className="font-mono text-sm"
                        placeholder={webhookConfigured ? t('settings.payment.secretKeepPlaceholder') : webhookPlaceholder}
                        value={newWebhookSecret}
                        onChange={(e) => onWebhookSecretChange(config.key, e.target.value)}
                        autoComplete="new-password"
                        helper={!webhookConfigured && !newWebhookSecret && isEnabled && webhookRequired
                            ? t('settings.payment.secretRequiredWhenEnabled', { label: webhookLabel, name: config.name })
                            : config.webhookOptional && !webhookConfigured && !newWebhookSecret
                                ? t('settings.payment.webhookOptionalHint')
                                : undefined}
                    />
                )}

                <div className="flex flex-wrap items-center gap-3">
                    <Button
                        variant="secondary"
                        onClick={() => onTestConnection(config.key)}
                        disabled={cardTest.loading || (!canEnable && !apiKeyConfigured)}
                    >
                        {cardTest.loading ? t('settings.payment.testing') : t('settings.payment.testConnection')}
                    </Button>
                    {cardTest.message && (
                        <p className={`text-xs ${cardTest.success ? 'text-emerald-700' : 'text-red-600'}`}>
                            {cardTest.message}
                        </p>
                    )}
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-700 space-y-1">
                    <p className="font-semibold">{t('settings.payment.webhookUrlTitle', { name: config.name })}</p>
                    <p className="font-mono break-all">{webhookUrl}</p>
                    {config.webhookEvents && (
                        <p className="text-slate-500">{t('settings.payment.subscribeTo')} <span className="font-mono">{config.webhookEvents}</span></p>
                    )}
                    <p className="text-slate-500 pt-1">
                        {t('settings.payment.keysFrom')}{' '}
                        <a href={config.keysUrl} target="_blank" rel="noreferrer" className="underline hover:text-slate-800">
                            {config.keysUrl.replace('https://', '')}
                        </a>
                    </p>
                </div>

                {!currencyOk && (
                    <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        {t('settings.payment.currencyUnsupported', { name: config.name, currency: currencyCode })}
                    </p>
                )}
                {!httpsOk && (
                    <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        {t('settings.payment.httpsGatewayHint', { name: config.name })}
                    </p>
                )}
                {isEnabled && (!canEnable || !canEnableWebhook || !currencyOk || !httpsOk) && (
                    <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        {t('settings.payment.resolveWarnings', { name: config.name })}
                    </p>
                )}
            </div>
        </div>
    );
}

export default function PaymentMethodSettings() {
    const { t } = useTranslation();
    const { settings } = useRegionalSettings();
    const countryProfile = getCountryProfile(settings?.countryCode);
    const [allMethods, setAllMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [newSecrets, setNewSecrets] = useState({});
    const [testResults, setTestResults] = useState({});
    const [overview, setOverview] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);

    const webhookBaseUrl = useMemo(() => {
        const fromOverview = overview?.publicBaseUrl;
        if (fromOverview) {
            return String(fromOverview).replace(/\/+$/, '');
        }
        if (typeof window !== 'undefined' && window.location?.origin) {
            return window.location.origin;
        }
        return 'https://your-academy-domain.com';
    }, [overview?.publicBaseUrl]);

    const resolveWebhookUrl = (provider, path) => {
        const key = String(provider || '').toLowerCase();
        const fromMap = overview?.webhookUrls?.[key];
        if (fromMap && !String(fromMap).startsWith('(')) {
            return fromMap;
        }
        return `${webhookBaseUrl}${path}`;
    };

    const loadOverview = async () => {
        try {
            const data = await getPaymentGatewayOverview();
            setOverview(data);
        } catch {
            setError((prev) => prev || t('settings.payment.loadFailed'));
            // Keep last good overview so license/HTTPS gates do not flip to "ok"
        }
    };

    useEffect(() => {
        let active = true;
        setLoadFailed(false);
        Promise.all([getAdminPaymentMethods(), getPaymentGatewayOverview()])
            .then(([rows, overviewData]) => {
                if (!active) return;
                setAllMethods(Array.isArray(rows) ? rows : []);
                setOverview(overviewData);
                setError('');
            })
            .catch(() => {
                if (!active) return;
                setLoadFailed(true);
                setError(t('settings.payment.loadFailed'));
            })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [t]);

    const gatewayMethods = useMemo(() => {
        const map = {};
        GATEWAYS.forEach((g) => {
            map[g.key] = allMethods.find((m) => m.methodKey === g.key) || {
                methodKey: g.key,
                displayName: g.name,
                gatewayProvider: g.provider,
                enabled: false,
                visibleToStudents: false,
                methodKind: 'GATEWAY',
            };
        });
        return map;
    }, [allMethods]);

    const updateGateway = (key, field, value) => {
        setAllMethods((prev) => {
            const exists = prev.some((m) => m.methodKey === key);
            if (!exists) {
                const config = GATEWAYS.find((g) => g.key === key);
                return [...prev, {
                    methodKey: key,
                    displayName: config?.name || key,
                    gatewayProvider: config?.provider,
                    enabled: false,
                    visibleToStudents: false,
                    methodKind: 'GATEWAY',
                    [field]: value,
                }];
            }
            return prev.map((m) => (m.methodKey === key ? { ...m, [field]: value } : m));
        });
    };

    const manualMethods = useMemo(
        () => allMethods.filter((m) => m.methodKind !== 'GATEWAY'),
        [allMethods],
    );

    const visibleGatewayConfigs = useMemo(
        () => GATEWAYS.filter((g) => countryProfile.gateways.includes(g.key)),
        [countryProfile.gateways],
    );

    const manualEnabledCount = useMemo(
        () => manualMethods.filter((m) => m.enabled).length,
        [manualMethods],
    );

    const gatewayEnabledCount = useMemo(
        () => visibleGatewayConfigs.filter((g) => gatewayMethods[g.key]?.enabled).length,
        [visibleGatewayConfigs, gatewayMethods],
    );

    const handleManualToggle = (key, checked) => {
        setAllMethods((prev) =>
            prev.map((m) =>
                m.methodKey === key
                    ? { ...m, enabled: checked, visibleToStaff: checked || m.visibleToStaff }
                    : m,
            ),
        );
    };

    const handleToggle = (key, checked) => {
        setAllMethods((prev) => {
            const exists = prev.some((m) => m.methodKey === key);
            const config = GATEWAYS.find((g) => g.key === key);
            if (!exists) {
                return [...prev, {
                    methodKey: key,
                    displayName: config?.name,
                    gatewayProvider: config?.provider,
                    enabled: checked,
                    visibleToStudents: checked,
                    methodKind: 'GATEWAY',
                }];
            }
            return prev.map((m) =>
                m.methodKey === key
                    ? { ...m, enabled: checked, visibleToStudents: checked, gatewayProvider: config?.provider }
                    : m
            );
        });
    };

    const testConnection = async (methodKey) => {
        const method = gatewayMethods[methodKey] || {};
        const newSecret = (newSecrets[methodKey] || '').trim();
        const newWebhook = (newSecrets[`${methodKey}_webhook`] || '').trim();
        setTestResults((prev) => ({ ...prev, [methodKey]: { loading: true, message: '' } }));
        setError('');
        try {
            const result = await testGatewayConnection({
                methodKey,
                gatewayKeyId: method.gatewayKeyId || '',
                gatewayKeySecret: newSecret || method.gatewayKeySecret || '',
                gatewayWebhookSecret: newWebhook || method.gatewayWebhookSecret || '',
            });
            setTestResults((prev) => ({
                ...prev,
                [methodKey]: {
                    loading: false,
                    success: !!result.success,
                    message: result.success ? t('settings.payment.connected') : t('settings.payment.connectionFailed'),
                },
            }));
        } catch (err) {
            setTestResults((prev) => ({
                ...prev,
                [methodKey]: {
                    loading: false,
                    success: false,
                    message: apiErrorMessage(err, t('settings.payment.connectionTestFailed')),
                },
            }));
        }
    };

    const dirtyValue = loading ? null : { allMethods, newSecrets };
    const persist = async () => {
        const payload = allMethods.map((m) => {
            const config = GATEWAYS.find((g) => g.key === m.methodKey);
            if (!config) return m;
            const newSecret = (newSecrets[m.methodKey] || '').trim();
            const newWebhook = (newSecrets[`${m.methodKey}_webhook`] || '').trim();
            return {
                ...m,
                gatewayProvider: config.provider,
                gatewayKeySecret: newSecret || m.gatewayKeySecret,
                gatewayWebhookSecret: newWebhook || m.gatewayWebhookSecret,
            };
        });
        const saved = await saveAdminPaymentMethods(payload);
        const nextMethods = Array.isArray(saved) ? saved : allMethods;
        setAllMethods(nextMethods);
        setNewSecrets({});
        await loadOverview();
        return { allMethods: nextMethods, newSecrets: {} };
    };
    const { markSaved } = useUnsavedChanges(dirtyValue, {
        enabled: !loading,
        onSave: async () => { await persist(); },
    });

    const save = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const next = await persist();
            markSaved(next);
            setSuccess(t('settings.payment.saveSuccess'));
        } catch (err) {
            setError(apiErrorMessage(err, t('settings.payment.saveFailed')));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <PageLoadingShell
                variant="page-shell-form"
                title={t('settings.payment.title')}
                subtitle={t('settings.payment.subtitle')}
            />
        );
    }

    if (loadFailed) {
        return (
            <div className="page-shell-form">
                <PageHeaderSection
                    eyebrow={t('nav.system')}
                    title={t('settings.payment.title')}
                    subtitle={t('settings.payment.subtitle')}
                />
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                    {error || t('settings.payment.loadFailed')}
                </div>
                <button
                    type="button"
                    className="inline-flex items-center rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
                    onClick={() => {
                        setLoading(true);
                        setLoadFailed(false);
                        setError('');
                        Promise.all([getAdminPaymentMethods(), getPaymentGatewayOverview()])
                            .then(([rows, overviewData]) => {
                                setAllMethods(Array.isArray(rows) ? rows : []);
                                setOverview(overviewData);
                            })
                            .catch(() => {
                                setLoadFailed(true);
                                setError(t('settings.payment.loadFailed'));
                            })
                            .finally(() => setLoading(false));
                    }}
                >
                    {t('common.retry', { defaultValue: 'Try again' })}
                </button>
            </div>
        );
    }

    const licensed = overview != null && overview.licenseOnlinePaymentsEnabled !== false;
    const httpsReady = overview != null && overview.httpsReady !== false;
    const productionHttps = overview?.productionHttps === true;
    const currencyCode = overview?.currencyCode || '';
    const recentEvents = Array.isArray(overview?.recentEvents) ? overview.recentEvents : [];

    return (
        <div className="page-shell-form">
            <PageHeaderSection
                eyebrow={t('nav.system')}
                title={t('settings.payment.title')}
                subtitle={t('settings.payment.subtitle')}
            />

            <form onSubmit={save} className="space-y-6">
                {success && (
                    <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                        {success}
                    </div>
                )}
                {error && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {error}
                    </div>
                )}

                {!licensed && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        <Trans
                            i18nKey="settings.payment.licenseRequired"
                            values={{
                                edition: overview?.licenseEdition
                                    ? t('settings.payment.licenseEditionSuffix', { edition: overview.licenseEdition })
                                    : '',
                            }}
                            components={{ strong: <strong /> }}
                        />
                    </div>
                )}

                {licensed && !httpsReady && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        <Trans
                            i18nKey="settings.payment.httpsRequired"
                            values={{ url: overview?.publicBaseUrl || t('common.notSet') }}
                            components={{ code: <code className="font-mono" /> }}
                        />
                    </div>
                )}

                {licensed && httpsReady && !productionHttps && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                        {t('settings.payment.localhostHttpsHint', {
                            url: overview?.publicBaseUrl || webhookBaseUrl,
                        })}
                    </div>
                )}

                {overview?.autopayBadgeLabel && (
                    <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
                        <p className="font-semibold">
                            {t('settings.payment.autopayCapabilityTitle', { defaultValue: 'Bank auto-pay' })}
                        </p>
                        <p className="mt-1">{overview.autopayBadgeLabel}</p>
                        <p className="mt-1 text-xs text-indigo-800/80">
                            {overview.autopayFeatureEnabled
                                ? t('settings.payment.autopayEnabledHint', {
                                    defaultValue: 'Feature flag is on — parents/students can link a bank account on the wallet page.',
                                })
                                : t('settings.payment.autopayDisabledHint', {
                                    defaultValue: 'Set AUTOPAY_ENABLED=true to let families link bank auto-pay (ACH / PAD / UPI Autopay by country).',
                                })}
                        </p>
                        {overview.autopayPullTiming && (
                            <p className="mt-1 text-xs text-indigo-800/80">
                                {t('settings.payment.autopayTimingHint', {
                                    defaultValue: 'Pull timing: {{timing}}{{grace}}',
                                    timing: overview.autopayPullTiming,
                                    grace: overview.autopayPullTiming === 'AFTER_GRACE_DAYS'
                                        ? ` (${overview.autopayGraceDays ?? 2} grace day(s))`
                                        : '',
                                })}
                            </p>
                        )}
                    </div>
                )}

                {/* Step 1 — Manual methods */}
                {manualMethods.length > 0 && (
                    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                                        {t('settings.payment.stepOne')}
                                    </p>
                                    <h2 className="mt-1 text-lg font-semibold text-slate-900">
                                        {t('settings.payment.manualMethods')}
                                    </h2>
                                    <p className="mt-1 max-w-2xl text-sm text-slate-600">
                                        {t('settings.payment.manualMethodsDesc')}
                                    </p>
                                </div>
                                <div className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-center">
                                    <p className="text-xs font-medium text-slate-500">{t('settings.report.enabledCountLabel')}</p>
                                    <p className="text-lg font-semibold tabular-nums text-slate-900">
                                        {manualEnabledCount}/{manualMethods.length}
                                    </p>
                                </div>
                            </div>
                            <p className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                                <Trans
                                    i18nKey="settings.payment.academyCurrency"
                                    values={{
                                        currency: currencyCode,
                                        country: settings?.countryName || settings?.countryCode || t('settings.payment.yourCountry'),
                                    }}
                                    components={{ strong: <strong /> }}
                                />
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-3 px-5 py-5 md:grid-cols-2 sm:px-6">
                            {manualMethods.map((method) => {
                                const on = !!method.enabled;
                                return (
                                    <label
                                        key={method.methodKey}
                                        className={`flex cursor-pointer gap-3 rounded-xl border p-4 transition-colors ${
                                            on
                                                ? 'border-primary-200 bg-primary-50/40 hover:border-primary-300'
                                                : 'border-slate-200 bg-slate-50/60 hover:border-slate-300'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                                            checked={on}
                                            onChange={(e) => handleManualToggle(method.methodKey, e.target.checked)}
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-sm font-semibold text-slate-900">
                                                    {method.displayName || method.methodKey}
                                                </span>
                                                <StatusChip on={on} t={t} />
                                            </div>
                                            <p className="mt-1 text-sm leading-snug text-slate-600">
                                                {method.instructions || t('settings.payment.manualMethodDefault')}
                                            </p>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Step 2 — Online gateways */}
                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                                    {t('settings.payment.stepTwo')}
                                </p>
                                <h2 className="mt-1 text-lg font-semibold text-slate-900">
                                    {t('settings.payment.gatewaysSection')}
                                </h2>
                                <p className="mt-1 max-w-2xl text-sm text-slate-600">
                                    {t('settings.payment.gatewaysSectionDesc')}
                                </p>
                            </div>
                            <div className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-center">
                                <p className="text-xs font-medium text-slate-500">{t('settings.report.enabledCountLabel')}</p>
                                <p className="text-lg font-semibold tabular-nums text-slate-900">
                                    {gatewayEnabledCount}/{visibleGatewayConfigs.length}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="space-y-4 px-5 py-5 sm:px-6">
                        {visibleGatewayConfigs.length === 0 ? (
                            <p className="text-sm text-slate-500">{t('settings.payment.noGatewaysForCountry')}</p>
                        ) : (
                            visibleGatewayConfigs.map((config) => (
                                <GatewayCard
                                    key={config.key}
                                    config={config}
                                    method={gatewayMethods[config.key]}
                                    newSecrets={newSecrets}
                                    testState={testResults[config.key]}
                                    onToggle={handleToggle}
                                    onFieldChange={updateGateway}
                                    onSecretChange={(key, value) => setNewSecrets((prev) => ({ ...prev, [key]: value }))}
                                    onWebhookSecretChange={(key, value) =>
                                        setNewSecrets((prev) => ({ ...prev, [`${key}_webhook`]: value }))
                                    }
                                    onTestConnection={testConnection}
                                    webhookUrl={resolveWebhookUrl(config.provider, config.webhookPath)}
                                    currencyCode={currencyCode}
                                    licensed={licensed}
                                    httpsReady={httpsReady}
                                />
                            ))
                        )}
                    </div>
                </section>

                {/* Step 3 — Activity + deployment */}
                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                            {t('settings.payment.stepThree')}
                        </p>
                        <h2 className="mt-1 text-lg font-semibold text-slate-900">
                            {t('settings.payment.recentActivity')}
                        </h2>
                        <p className="mt-1 max-w-2xl text-sm text-slate-600">
                            {t('settings.payment.recentActivityDesc')}
                        </p>
                    </div>
                    {recentEvents.length === 0 ? (
                        <p className="px-5 py-8 text-sm text-slate-500 sm:px-6">{t('settings.payment.noEvents')}</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-slate-500">
                                    <tr>
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">{t('settings.payment.when')}</th>
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">{t('settings.payment.gateway')}</th>
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">{t('settings.payment.event')}</th>
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">{t('settings.payment.status')}</th>
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">{t('settings.payment.student')}</th>
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">{t('settings.payment.detail')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {recentEvents.map((ev) => (
                                        <tr key={ev.id} className="text-slate-700">
                                            <td className="px-4 py-2.5 whitespace-nowrap text-xs">{ev.created ? formatDateTime(ev.created) : '—'}</td>
                                            <td className="px-4 py-2.5">{ev.gateway}</td>
                                            <td className="px-4 py-2.5 font-mono text-xs">{ev.eventType}</td>
                                            <td className="px-4 py-2.5">
                                                <span className={ev.status === 'SUCCESS' ? 'text-emerald-700' : ev.status === 'FAILED' ? 'text-red-600' : 'text-slate-500'}>
                                                    {t(`settings.payment.eventStatus.${ev.status}`, {
                                                        defaultValue: String(ev.status || '').replace(/_/g, ' '),
                                                    })}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2.5">{ev.studentId ?? '—'}</td>
                                            <td className="max-w-[220px] truncate px-4 py-2.5 text-xs" title={ev.detail || ''}>{ev.detail || ev.externalId || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div className="border-t border-slate-100 bg-slate-50/50 px-5 py-4 text-xs text-slate-600 space-y-1 sm:px-6">
                        <p className="font-semibold text-slate-800">{t('settings.payment.deploymentNote')}</p>
                        <p>
                            <Trans
                                i18nKey="settings.payment.deploymentHttps"
                                values={{ webhookBase: webhookBaseUrl }}
                                components={{ code: <code className="font-mono" /> }}
                            />
                        </p>
                        <p>
                            <Trans
                                i18nKey="settings.payment.deploymentFrontendUrl"
                                components={{ code: <code className="font-mono" /> }}
                            />
                        </p>
                    </div>
                </section>

                <div className="sticky bottom-4 z-10 flex justify-end">
                    <button
                        type="submit"
                        disabled={saving || !licensed}
                        className="rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                        {saving ? t('forms.common.saving') : t('settings.payment.saveSettings')}
                    </button>
                </div>
            </form>
        </div>
    );
}
